import { dpSharedStyleOnce } from './dp-shared.js'
// 配車・受注ビュー（中身/TMS・Supabase連動）。全社の受注/配車を一覧＋案件の新規入力・編集（VEILを案件入力の"正"に）。
// データ: trips（自社便・傭車）＋ orders（ハコベル＝近藤）。区分（全部/自社便/傭車）で切替。
// 案件入力: 「＋案件を追加」／日計表・手入力の行クリックで編集。得意先・協力会社・乗務員はマスタから選択（CD＋枝番→正式名称）。
//           管理IDはVEIL採番、source:'manual'。保存は Supabase upsert（trips）。取消は kind='other'（収支・ボードから除外／物理削除しない）。
import { listDocs, getOne, upsert, remove, COLLECTIONS, subscribeChanges } from '../data/collections.js'
import { supabaseReady } from '../supabase.js'
import { loadVehicleTypes, vehicleTypeInput } from '../data/vehicleTypes.js'
import { quoteOwnTrip, quoteFuelRate, QUOTE_MARGIN } from '../data/quoteOwnTrip.js'
import { listPlaceParts, splitJpAddress, sanitizeForCustomer } from '../data/intakeCore.js'
// 状態一本化 段1（オーナー決定 2026-08-28）: 仮受付→正式→配車済 を入口を問わず共通の1本に。
// 語彙・遷移・保存先の決めは statusUnify.js（純粋関数）に置く。
// 設計: engineering-タクミ/docs/2026-08-28-状態一本化-段1-設計.md
import { normalizeStatus, canTransition, saveTarget, STATUS_LABEL } from '../data/statusUnify.js'
import { openIdFromHash, pendingRequestRow } from '../data/crossLink.js'
// 暫定距離の自動計算（2026-08-28 オーナー決定: Google Routes API・認証つき Function 経由・キャッシュ優先）
import { shouldAutoKm, pairKey } from '../data/routeDistance.core.js'
import { getRouteKm } from '../data/routeDistance.js'

const YEN = (n) => '¥' + Math.round(Number(n) || 0).toLocaleString('ja-JP')
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const monthOf = (o) => o.month || (o.date ? String(o.date).slice(0, 7) : '')
const normCode = (c) => String(c == null ? '' : c).trim().replace(/^0+(?=\d)/, '')
const pad = (n, l = 2) => String(n).padStart(l, '0')
// 管理ID接頭辞＝入力者メールの1文字目＋3文字目（大文字）。例: d-kondo → DK（既存日計表IDと同体系）。
function emailPrefix(email) {
  const e = String(email || '').trim()
  return ((e[0] || 'X') + (e[2] || 'X')).toUpperCase()
}
// 衝突しないランダム末尾（base36・crypto優先）。
function randTail(n) {
  const cs = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let s = ''
  const buf = (globalThis.crypto && crypto.getRandomValues) ? crypto.getRandomValues(new Uint32Array(n)) : null
  for (let i = 0; i < n; i++) s += cs[(buf ? buf[i] : Math.floor(Math.random() * 36)) % 36]
  return s
}
// 管理ID＝[入力者メール1・3文字目(2)] + [運行日YYMMDD(6)] + [ランダム(6)] ＝ 14桁。
// 既存の「2文字コード＋YYMMDD＋6桁」体系を踏襲。末尾を乱数にして8人同時入力でも衝突しない。
function newManageId(dateStr, email) {
  const ymd = /^\d{4}-\d{2}-\d{2}$/.test(dateStr || '')
    ? String(dateStr).slice(2).replace(/-/g, '')
    : (() => { const d = new Date(); return `${pad(d.getFullYear() % 100)}${pad(d.getMonth() + 1)}${pad(d.getDate())}` })()
  return `${emailPrefix(email)}${ymd}${randTail(6)}`
}
const STAFF_BASE = ['片山', '山本', '近藤', '木下', '田中', '伊藤', '深瀬', '仙台営業所']
// ハコベル案件番号（管理NO）形。備考①がこの形なら ankenNo にも入れる（跨ぎ重複の突合キー）。
const HAKO_NO = /^\d{6}-[0-9A-Za-z]{1,6}-[0-9A-Za-z]{1,6}$/
// 都道府県（ハコベル寄せの住所分解用）
const PREFS = ['北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県', '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県', '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県', '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県', '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県']
// 地点（stops）ユーティリティ。1件 = {role:'pick'|'drop', name,pref,city,addr,bldg,date,time,spec,contact,tel,note}。積地・着地を訪問順に複数保持。
const blankStop = (role) => ({ role, name: '', pref: '', city: '', addr: '', bldg: '', date: '', time: '', spec: '', contact: '', tel: '', note: '' })
const stopAddr = (s) => [s.pref, s.city, s.addr, s.bldg].map((x) => String(x == null ? '' : x).trim()).filter(Boolean).join('') || String(s.name || '').trim()
const stopsOf = (f) => Array.isArray(f.stops) ? f.stops : []
const picksOf = (f) => stopsOf(f).filter((s) => s.role === 'pick')
const dropsOf = (f) => stopsOf(f).filter((s) => s.role === 'drop')
const firstPick = (f) => picksOf(f)[0] || null
const lastDrop = (f) => { const d = dropsOf(f); return d.length ? d[d.length - 1] : null }
// 発＝最初の積地／着＝最後の着地（収支・帳票の互換用に合成文字列を維持）。旧データは旧origin/destへフォールバック。
const originStr = (f) => { const s = firstPick(f); return s ? stopAddr(s) : String(f.origin || '').trim() }
const destStr = (f) => { const s = lastDrop(f); return s ? stopAddr(s) : String(f.dest || '').trim() }
const runDateOf = (f) => { const s = firstPick(f); return (s && s.date) || f.date || '' }

const D = {
  el: null, month: null, months: [], rows: [], online: false, fetchError: false, src: 'all',
  // セグメント（状態タブ）: all/sashi/formal/sent。一覧の絞り込みのみに使う（KPIの母数には影響しない）
  // （設計 方針2・PR④。intake.js の統一状態語彙 仮受付/正式/配車済 を踏襲）
  seg: 'all',
  sort: { key: 'startDate', dir: 'desc' },
  tripsRaw: [], orders: [], cust: [], part: [], drv: [], custById: {}, partById: {}, drvById: {},
  vtypes: [],
  // 未取込の requests（仮受付/正式）。表示専用。KPI（n/amt/toll/cO/cC/cManual）には混ぜない
  // （方針2・pm-ミチ「仮受付は KPI に混ぜない」の拡張適用）。DBは触らない
  // reqsRaw ＝ 未取込 request の生doc（行クリック編集・保存の差分反映用）。pendingRequests はその表示行
  reqsRaw: [],
  pendingRequests: [],
  form: null, saving: '',
  user: null, myStaff: '', scope: 'all', live: false, rtOff: null, ctx: null, _scopeTouched: false,
}
const CAP = 1200

const byCode = (a, b) => (Number(a.code) || 0) - (Number(b.code) || 0) || String(a.code || a.id).localeCompare(String(b.code || b.id), 'ja')
const idMap = (arr) => { const m = {}; for (const x of arr) m[x.id] = x; return m }

// ハコベル受注（近藤の配車）→ 統一行。実運送がエースジャパン＝自社便、それ以外＝傭車。
function fromOrder(o) {
  const own = /エースジャパン/.test(o.carrier || '')
  return { _trip: false, source: own ? '自社便' : '傭車', via: 'ハコベル', id: o.id, staff: (o.staff || '近藤').split(/[ 　]/)[0],
    shipper: o.shipper, startDate: o.startDate, startTime: o.startTime, startPref: o.startPref, startCity: o.startCity,
    endPref: o.endPref, endCity: o.endCity, item: o.item, vehReq: o.vehReq, vehSize: o.vehSize,
    orderAmount: Number(o.orderAmount) || 0, billToll: Number(o.billToll) || 0,
    partner: own ? '' : (o.carrier || ''), driver: o.driver, vehicle: o.vehicle, status: o.status || '', month: o.month || '' }
}
// trip（自社便/傭車） → 統一行
function fromTrip(t) {
  const own = t.kind === 'own'
  const stops = Array.isArray(t.stops) ? t.stops : []
  const pick = stops.find((s) => s && s.role === 'pick') || null
  const drops = stops.filter((s) => s && s.role === 'drop')
  const drop = drops.length ? drops[drops.length - 1] : null
  const from = listPlaceParts(pick, t.origin || t.fromPlace || '')
  const to = listPlaceParts(drop, t.dest || t.toPlace || '')
  return { _trip: true, source: own ? '自社便' : '傭車', via: t.source === 'manual' ? '手入力' : '日計表', id: t.id, staff: t.staff || '',
    shipper: t.customerFormal || t.customerName || '', startDate: t.date || '', startTime: '',
    startPref: from.pref, startCity: from.city, endPref: to.pref, endCity: to.city,
    item: t.item || '', vehReq: t.vehicleType || t.vehicleClass || '', vehSize: '',
    orderAmount: Number(t.fare) || 0, billToll: Number(t.reqToll != null ? t.reqToll : t.tollBill) || 0,
    partner: t.partnerFormal || t.partnerName || '', driver: t.driverName || t.ownDriver || t.youshaDriver || '',
    vehicle: t.carNo || t.vehicleId || '', status: '運行実績', month: monthOf(t) }
}

function styleOnce() {
  dpSharedStyleOnce()
  if (document.getElementById('dp-style')) return
  const s = document.createElement('style'); s.id = 'dp-style'
  s.textContent = `
  .dp{display:flex;flex-direction:column;gap:12px}
  .dp-bar{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
  .dp-bar select{font-family:inherit;font-size:13px;padding:6px 10px;border:1px solid var(--e1);border-radius:8px;background:var(--card);color:var(--text)}.dp-btn.primary:hover{background:var(--accent-fill)}.dp-btn.danger{background:var(--neg);color:var(--bg);border-color:var(--neg)}
  .dp-save{font-size:11px;color:var(--pos);background:var(--head);border:1px solid var(--e1);border-radius:999px;padding:3px 10px}
  .dp-kpis{display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:10px}
  .dp-kpi{background:var(--card);border:1px solid var(--e1);border-radius:10px;padding:11px 13px}
  .dp-kpi .k{font-size:10.5px;color:var(--muted)}.dp-kpi .v{font-size:19px;font-weight:700;font-variant-numeric:tabular-nums;margin-top:2px}
  .dp-kpi.acc .v{color:var(--text)}
  .dp-wrap{border:1px solid var(--e1);border-radius:12px;overflow:hidden;background:var(--card)}
  .dp-wrap .cap{padding:9px 13px;font-size:12px;font-weight:700;color:var(--muted);background:var(--head);border-bottom:1px solid var(--e1)}
  .dp-scroll{max-height:60vh;overflow:auto}
  table.dp-tbl{width:100%;border-collapse:collapse;font-size:11.5px}
  .dp-tbl th,.dp-tbl td{padding:6px 9px;border-bottom:1px solid var(--e1);text-align:left;white-space:nowrap}
  .dp-tbl th{position:sticky;top:0;background:var(--head);color:var(--text);font-size:10.5px;z-index:1;cursor:pointer;user-select:none}
  .dp-tbl th:hover{background:var(--head);color:var(--text)}
  .dp-tbl td.n,.dp-tbl th.n{text-align:right;font-variant-numeric:tabular-nums}
  .dp-tbl th .arw{color:var(--text);font-size:9px;margin-left:2px}
  .dp-tbl tbody tr.clk{cursor:pointer}.dp-tbl tbody tr.clk:hover td{background:var(--card)}
  .dp-badge{font-size:9.5px;font-weight:700;border-radius:4px;padding:1px 6px}
  .dp-via{font-size:9px;color:var(--text);margin-left:4px}
  .dp-combo-row .cn{color:var(--text);overflow:hidden;text-overflow:ellipsis}
  /* ハコベル寄せ: 2ペイン（左=フォーム／右=入力チェックの固定パネル） */
  .dp-card.wide{width:min(1120px,97vw)}
  .dp-goto:last-child{border-bottom:0}
  .dp-goto:hover{background:var(--card)}
  .dp-goto .st{font-size:11px;font-weight:800;white-space:nowrap}
  .dp-side-sum .r{display:flex;justify-content:space-between;align-items:baseline;gap:8px;font-size:12px;margin-top:7px}
  .dp-side-sum .r .v{font-weight:800;font-variant-numeric:tabular-nums}
  .dp-side-sum .r.ok .v{color:var(--pos)}
  .dp-side-sum .r.ng .v{color:var(--neg)}
  .dp-side-quote{margin-top:10px;padding-top:8px;border-top:1px solid var(--e1)}
  .dp-side-sum .hint{font-size:10px;color:var(--muted);margin-top:6px;line-height:1.45}
  .dp-ptbtn.del{font-size:13px;padding:1px 8px}
  .dp-addbtn:hover{filter:brightness(.97)}
  .dp-radio input{accent-color:var(--text)}
  .dp-more>summary::-webkit-details-marker{display:none}
  .dp-more>summary::before{content:'▸ ';color:var(--text)}
  .dp-more[open]>summary::before{content:'▾ '}
  `
  document.head.appendChild(s)
}

export async function renderDispatch(el, ctx = {}) {
  styleOnce()
  D.el = el; D.ctx = ctx; D.user = ctx.user || null
  D.online = !ctx.demo && supabaseReady
  D.fetchError = false
  D.form = null; D.saving = ''
  el.innerHTML = '<div class="ct-state is-loading"><b class="ct-state__t">読み込んでいます…</b><span>受注・配車一覧を読み込んでいます</span></div>'
  D.tripsRaw = []; D.orders = []; D.cust = []; D.part = []; D.drv = []; D.reqsRaw = []; D.pendingRequests = []
  if (D.online) {
    try {
      const [orders, trips, cust, part, drv, vtypes, requests] = await Promise.all([
        listDocs(COLLECTIONS.orders), listDocs(COLLECTIONS.trips), listDocs(COLLECTIONS.customers), listDocs(COLLECTIONS.partners), listDocs(COLLECTIONS.drivers), loadVehicleTypes(), listDocs(COLLECTIONS.requests),
      ])
      D.orders = orders || []; D.tripsRaw = trips || []; D.vtypes = vtypes || []
      // 未取込（tripId 無し）だけを一覧に混ぜる。取込済は trips 側に既に居るので二重表示しない
      D.reqsRaw = (requests || []).filter((r) => !r.tripId)
      D.cust = (cust || []).slice().sort(byCode); D.part = (part || []).slice().sort(byCode); D.drv = (drv || []).slice().sort(byCode)
      D.custById = idMap(D.cust); D.partById = idMap(D.part); D.drvById = idMap(D.drv)
      // 各自入力: ログインユーザーの担当を users から取得（初回未設定は担当バーで選択）
      if (D.user && D.user.email) { try { const me = await getOne('users', D.user.email); D.myStaff = (me && me.staff) || '' } catch (e) {} }
      if (D.myStaff && !D._scopeTouched) D.scope = 'mine'
      startRealtime()
    } catch { D.online = false; D.fetchError = true }
  }
  rebuildRows()
  // 受付側から「配車ボードで開く」で飛んできた場合（#/dispatch?open=<tripId>）は自動で開く。
  // 統合の導線（PR②③・design参照）。まだ該当が無ければ何もしない（一覧が通常どおり出る）
  const openId = openIdFromHash(location.hash)
  if (openId) {
    const t = D.tripsRaw.find((x) => x.id === openId)
    if (t) D.form = tripToForm(t)
  }
  paint(ctx)
}

// リアルタイム: trips/orders の変更を購読し、ボードへ差分反映（更新ボタン不要）。購読は一度だけ。
function startRealtime() {
  if (D.rtOff) return
  const patch = (arr, id, doc) => {
    const i = arr.findIndex((x) => x.id === id)
    if (doc) { if (i >= 0) arr[i] = doc; else arr.push(doc) } else if (i >= 0) arr.splice(i, 1)
  }
  const offT = subscribeChanges('trips', ({ id, doc }) => { if (!id) return; patch(D.tripsRaw, id, doc); D.live = true; if (!D.form) { rebuildRows(); paint(D.ctx) } })
  const offO = subscribeChanges('orders', ({ id, doc }) => { if (!id) return; patch(D.orders, id, doc); D.live = true; if (!D.form) { rebuildRows(); paint(D.ctx) } })
  D.rtOff = () => { try { offT() } catch (e) {} try { offO() } catch (e) {} }
}

function rebuildRows() {
  const rows = [
    ...D.tripsRaw.filter((t) => t.kind === 'own' || t.kind === 'charter').map(fromTrip),
    ...D.orders.filter((o) => !/エースジャパン/.test(o.carrier || '')).map(fromOrder),
  ]
  D.rows = rows
  D.pendingRequests = (D.reqsRaw || []).map(pendingRequestRow)
  // 月の選択肢は「表示するもの全部」（配車済＋仮受付/正式）から作る。KPIの母数（D.rows）とは別
  const ms = [...new Set([...rows, ...(D.pendingRequests || [])].map((r) => r.month).filter(Boolean))].sort()
  D.months = ms
  if (!D.month || !ms.includes(D.month)) D.month = ms[ms.length - 1] || null
}

// ── 案件フォーム（追加/編集） ──────────────────────────────
function resolveCust(t) {
  if (t.customerCode != null && t.customerCode !== '') { const hit = D.cust.find((c) => String(c.code) === String(t.customerCode) && (t.customerBranchNo == null || String(c.branchNo == null ? '' : c.branchNo) === String(t.customerBranchNo))); if (hit) return hit.id }
  const nm = t.customerFormal || t.customerName; if (nm) { const hit = D.cust.find((c) => (c.formal || '') === nm || (c.name || '') === nm); if (hit) return hit.id }
  return ''
}
function resolvePart(t) {
  if (t.partnerCode != null && t.partnerCode !== '') { const hit = D.part.find((c) => String(c.code) === String(t.partnerCode) && (t.partnerBranchNo == null || String(c.branchNo == null ? '' : c.branchNo) === String(t.partnerBranchNo))); if (hit) return hit.id }
  const nm = t.partnerFormal || t.partnerName; if (nm) { const hit = D.part.find((c) => (c.formal || '') === nm || (c.name || '') === nm); if (hit) return hit.id }
  return ''
}
function resolveDrv(t) {
  const code = normCode(t.driverCode); if (code) { const hit = D.drv.find((d) => normCode(d.code || d.id) === code); if (hit) return hit.id }
  const nm = t.driverName; if (nm) { const hit = D.drv.find((d) => (d.name || '') === nm); if (hit) return hit.id }
  return ''
}
function blankForm(kind = 'own', carry = {}) {
  // status の既定は sent（配車済）＝現行挙動と同じ確定側。既存ユーザーの入力は1クリックも増えない。
  // 連続入力は日付・得意先と同様に状態も引き継ぐ（電話の仮の話を連投する使い方）。
  return { mode: 'add', id: '', source: 'manual', kind, tripType: 'spot',
    status: carry.status || 'sent', savedStatus: '', isRequest: false,
    date: carry.date || '', unloadDate: '', salesDate: '', receiptDate: '', staff: D.myStaff || carry.staff || '',
    customerId: carry.customerId || '', customerName: carry.customerName || '',
    origin: '', dest: '', item: '', vehicleClass: carry.vehicleClass || '', memo1: '', memo2: '',
    loadTime: '', unloadTime: '', packing: '', qty: '', weightKg: '', volumeM3: '', prime: '', slipNo: '',
    stops: [Object.assign(blankStop('pick'), { date: carry.date || '' }), blankStop('drop')], cargoNote: '',
    driverId: carry.driverId || '', driverName: '', carNo: carry.carNo || '', fare: '', tollBill: '', billFixed: '',
    partnerId: carry.partnerId || '', partnerName: carry.partnerName || '', carrier: '', youshaDriver: '', tel: '', charterCost: '', tollPay: '', payFixed: '',
    quoteKm: '', quoteDurMin: '',
    err: '', confirmVoid: false, cont: true }
}
// trip → フォームの stops配列。新データは t.stops をそのまま、旧データは origin/dest（1行）→積地1・着地1。
function tripStops(t) {
  if (Array.isArray(t.stops) && t.stops.length) {
    return t.stops.map((s) => Object.assign(blankStop(s.role === 'drop' ? 'drop' : 'pick'), {
      name: s.name || '', pref: s.pref || '', city: s.city || '', addr: s.addr || '', bldg: s.bldg || '',
      date: s.date || '', time: s.time || '', spec: s.spec || '', contact: s.contact || '', tel: s.tel || '', note: s.note || '',
    }))
  }
  const fromText = (role, text, extra) => {
    const stop = Object.assign(blankStop(role), extra)
    const parsed = splitJpAddress(text)
    if (parsed && (parsed.pref || parsed.city)) {
      stop.pref = parsed.pref || ''
      stop.city = parsed.city || ''
      stop.addr = parsed.addr || ''
      if (!(parsed.pref || parsed.city) || parsed.addr) stop.name = text
      else if (String(text || '').trim() !== `${parsed.pref}${parsed.city}`) stop.name = text
    } else {
      stop.name = text || ''
      if (text) stop.city = String(text).trim()
    }
    return stop
  }
  return [
    fromText('pick', t.origin || t.fromPlace || '', { date: t.date || '', time: t.loadTime || '', spec: t.loadSpec || '' }),
    fromText('drop', t.dest || t.toPlace || '', { date: t.unloadDate || '', time: t.unloadTime || '', spec: t.unloadSpec || '' }),
  ]
}
function tripToForm(t) {
  // 既存 trip ＝ 配車済（無指定も配車済扱い＝マイグレーション不要）。savedStatus='sent' なので
  // canTransition により状態の選択肢は出ない（配車済からは戻れない。取り下げは既存の「取消」）。
  return { mode: 'edit', id: t.id, source: t.source || '', kind: t.kind === 'charter' ? 'charter' : 'own', tripType: t.tripType || 'spot', cont: false,
    status: 'sent', savedStatus: 'sent', isRequest: false,
    date: t.date || '', unloadDate: t.unloadDate || '', salesDate: t.salesDate || '', receiptDate: t.receiptDate || '', staff: t.staff || '',
    customerId: resolveCust(t), customerName: t.customerFormal || t.customerName || '',
    origin: t.origin || t.fromPlace || '', dest: t.dest || t.toPlace || '', item: t.item || '',
    loadTime: t.loadTime || '', unloadTime: t.unloadTime || '', packing: t.packing || '', qty: t.qty != null ? t.qty : '', weightKg: t.weightKg != null ? t.weightKg : '', volumeM3: t.volumeM3 != null ? t.volumeM3 : '', prime: t.prime || '', slipNo: t.slipNo || '',
    stops: tripStops(t), cargoNote: t.cargoNote || '',
    vehicleClass: t.vehicleClass || t.vehicleType || '', memo1: t.memo1 || t.ankenNo || '', memo2: t.memo2 || '',
    driverId: resolveDrv(t), driverName: t.driverName || '',
    carNo: t.carNo || t.vehicleId || '', fare: t.fare != null ? t.fare : '', tollBill: t.tollBill != null ? t.tollBill : '', billFixed: t.billFixed || '',
    partnerId: resolvePart(t), partnerName: t.partnerFormal || t.partnerName || '',
    carrier: t.carrier || '', youshaDriver: t.youshaDriver || t.charterDriver || '', tel: t.tel || '',
    charterCost: t.charterCost != null ? t.charterCost : '', tollPay: t.tollPay != null ? t.tollPay : '', payFixed: t.payFixed || '',
    quoteKm: '', quoteDurMin: '',
    err: '', confirmVoid: false }
}
// 未取込 request（手入力の仮受付/正式・entry:'dispatch'）→ 配車フォーム。
// フィールド語彙は trips と共通なので tripToForm をそのまま流用し、状態だけ上書きする。
function requestToDispForm(r) {
  const f = tripToForm(r)
  f.isRequest = true
  f.status = normalizeStatus(r.status)
  f.savedStatus = f.status
  f.source = r.source || 'manual'
  return f
}

const optList = (arr, sel, fmt) => ['<option value=""></option>'].concat(arr.map((x) => `<option value="${esc(x.id)}"${x.id === sel ? ' selected' : ''}>${esc(fmt(x))}</option>`)).join('')
const partyFmt = (c) => `${c.code != null ? c.code : ''}${c.branchNo != null && c.branchNo !== '' ? '-' + c.branchNo : ''}  ${c.formal || c.name || c.id}`
// 会社名でも検索できるよう「名前 CD-枝番」でサジェスト表示。入力値→マスタ解決（名前/CD/表示文字のいずれでも）。
const partyDisplay = (c) => `${c.formal || c.name || c.id}　${c.code != null ? c.code : ''}${c.branchNo != null && c.branchNo !== '' ? '-' + c.branchNo : ''}`.trim()
function resolveParty(arr, value) {
  const v = String(value == null ? '' : value).trim()
  if (!v) return null
  return arr.find((c) => partyDisplay(c) === v)
    || arr.find((c) => (c.formal || '') === v || (c.name || '') === v)
    || arr.find((c) => String(c.code) === v || `${c.code}-${c.branchNo}` === v)
    || null
}
// combo の blur 解決。得意先/協力会社は resolveParty、乗務員は氏名/コード/表示文字で解決。
function resolveCombo(which, arr, value) {
  const v = String(value == null ? '' : value).trim()
  if (!v) return null
  if (which !== 'drv') return resolveParty(arr, value)
  return arr.find((d) => (d.name || '') === v)
    || arr.find((d) => normCode(d.code || d.id) === normCode(v))
    || arr.find((d) => `${d.code != null ? d.code : (d.id || '')}　${d.name || ''}` === v)
    || null
}
// 乗務員コンボの初期表示：マスタ確定なら「コード　氏名」、自由入力なら氏名そのまま。
function driverInit(f) {
  if (f.driverId) { const d = D.drvById[f.driverId]; if (d) return `${d.code != null ? d.code : d.id}　${d.name || ''}` }
  return f.driverName || ''
}
const drvFmt = (d) => `${d.code || d.id}  ${d.name || ''}`
function field(label, control, hint) {
  return `<label class="dp-fld"><span class="dp-lab">${esc(label)}</span>${control}${hint ? `<span class="dp-hint">${hint}</span>` : ''}</label>`
}
// 右側スティッキーパネル（ハコベルの入力チェック相当）。荷主/ルート/運行日/車種/荷物/担当/運賃の充足を✓・未入力で表示＋依頼サマリ。
function sidePanel(f) {
  const own = f.kind === 'own'
  const partyOk = own ? !!(f.driverId || f.driverName) : !!(f.partnerId || f.partnerName)
  const items = [
    ['荷主', !!f.customerName, 'sec-nushi'],
    ['ルート', picksOf(f).some((s) => stopAddr(s)) && dropsOf(f).some((s) => stopAddr(s)), 'sec-route'],
    ['運行日', !!runDateOf(f), 'sec-route'],
    ['車種', !!f.vehicleClass, 'sec-sha'],
    ['荷物（品名）', !!f.item, 'sec-nimotsu'],
    [own ? '乗務員' : '協力会社', partyOk, 'sec-tanto'],
    ['運賃', f.fare !== '' && f.fare != null, 'sec-kingaku'],
  ]
  const ng = items.filter((x) => !x[1]).length
  const rows = items.map(([k, ok, go]) => `<div class="dp-goto" data-goto="${go}"><span>${esc(k)}</span><span class="st ${ok ? 'ok' : 'no'}">${ok ? '✓ 入力済' : '未入力'}</span></div>`).join('')
  const partyLine = own ? (f.driverName || (f.driverId && D.drvById[f.driverId] ? D.drvById[f.driverId].name : '') || '—') : (f.partnerName || '—')
  const sub = own ? (Number(f.fare) || 0) : (Number(f.charterCost) || 0)
  const drop = lastDrop(f)
  const pick = firstPick(f)
  const q = own ? quoteOwnTrip({
    fare: f.fare, vehicleClass: f.vehicleClass,
    date: runDateOf(f), unloadDate: (drop && drop.date) || f.unloadDate || '',
    km: f.quoteKm,
    // 所要時間ベースの人件費（2026-08-28）。quoteDurMin は暫定距離と同時に Google から取得した隠しデータ
    durationMin: f.quoteDurMin,
    stopCount: stopsOf(f).filter((s) => stopAddr(s)).length, // 積卸バッファ×地点数（住所のある地点）
    loadTime: (pick && pick.time) || f.loadTime || '',       // 最初の積地の時刻（深夜判定）
    unloadTime: (drop && drop.time) || f.unloadTime || '',   // 最後の卸地の時刻（深夜判定）
  }) : null
  // 人件費の方式を明示（2026-08-28 所要時間ベース）: 「8.3h×2,400円＋割増」vs「35%（時間未取得）」
  const laborLabel = q && q.laborMode === 'time'
    ? `人件費 ${q.laborTime.hours}h×${q.laborTime.hourly.toLocaleString('ja-JP')}円${(q.laborTime.overH > 0 || q.laborTime.surchargePct > 0) ? '＋割増' : ''}`
    : q ? `人件費 ${Math.round(q.laborRate * 100)}%（時間未取得）` : ''
  const quoteBlock = q ? `
      <div class="dp-side-quote">
        <div class="cap">経費・損益分岐（試算）</div>
        <div class="r"><span>${laborLabel}</span><span class="v">${YEN(q.labor)}</span></div>
        <div class="r"><span>車両経費${q.days ? ` ${q.days}営業日` : ''}</span><span class="v">${q.days ? YEN(q.carCost) : '日付未入力'}</span></div>
        <div class="r"><span>燃料 ${q.kmPerL}km/L・${q.yenPerL}円/L</span><span class="v">${q.fuelKnown ? YEN(q.fuel) : '距離未入力'}</span></div>
        <div class="r"><span>運行経費</span><span class="v">${YEN(q.runCost)}</span></div>
        <div class="r"><span>損益分岐運賃</span><span class="v">${YEN(q.breakEven)}</span></div>
        <div class="r"><span>目安運賃（経費×${Math.round(QUOTE_MARGIN * 100)}%）</span><span class="v">${YEN(q.suggestedFare)}</span></div>
        <div class="r ${q.aboveBreakEven ? 'ok' : 'ng'}"><span>この運賃の収支</span><span class="v">${q.contrib < 0 ? '−' : ''}${YEN(Math.abs(q.contrib))}</span></div>
        <div class="hint">分岐（目安運賃）は運行経費（車両${q.fuelKnown ? '＋燃料' : ''}）の ${Math.round(QUOTE_MARGIN * 100)}% 乗せ。燃料は ${q.yenPerKm}円/km（${q.vClass === 'large' ? '大型' : '中型'}の試算燃費。ダッシュボードの前提で変更可。デジタコ実測とは別）。</div>
      </div>` : ''
  return `
    ${ng ? `<div class="dp-side-warn">⚠ 未入力の項目が ${ng} 件あります</div>` : '<div class="dp-side-ok">✓ 必要項目はすべて入力済みです</div>'}
    <div class="dp-side-box">${rows}</div>
    <div class="dp-side-sum">
      <div class="cap">受注 / 依頼</div>
      <div class="r"><span>${own ? '乗務員' : '協力会社'}</span><span class="v" style="font-size:12px;text-align:right">${esc(partyLine)}</span></div>
      <div class="r"><span>${own ? '運賃（受注）' : '傭車（支払）'}</span><span class="v" style="font-size:15px">${YEN(sub)}</span></div>
      ${quoteBlock}
    </div>`
}
function updateCheck() { const box = document.getElementById('dp-check'); if (box && D.form) box.innerHTML = sidePanel(D.form) }

// ── 暫定距離の自動プリフィル（2026-08-28 オーナー決定: Google Routes API）──
// 自社便で、最初の積地と最後の卸地の両方に都道府県+市区町村が入り、距離欄が空のときだけ
// 自動取得して埋める。**手入力の値は絶対に上書きしない**（消せば再取得する）。
// デモ/未サインイン（!D.online）は呼ばない。取得失敗は静かに手入力のまま＋短い注記。
// 条件判定は routeDistance.core.js の純ロジック（test_route_distance_core.mjs でテスト）。
let autoKmTimer = 0
const autoKm = { pending: '', failed: '' } // pairKey 単位で在飛行・失敗を覚え、同一ペアの連打を抑制
function setKmNote(text) {
  if (D.form) D.form.kmNote = text || '' // フォームに持たせる＝開き直せば消える。保存 doc には入らない（saveForm は明示的に組む）
  const el = D.el && D.el.querySelector('#dp-kmauto')
  if (el) el.textContent = text ? '　' + text : ''
}
function scheduleAutoKm() { clearTimeout(autoKmTimer); autoKmTimer = setTimeout(maybeAutoKm, 600) }
async function maybeAutoKm() {
  const f = D.form
  if (!f) return
  const ep = shouldAutoKm({ kind: f.kind, online: D.online, quoteKm: f.quoteKm, stops: stopsOf(f) })
  if (!ep) return
  const key = pairKey(ep.origin, ep.dest)
  if (!key || autoKm.pending === key || autoKm.failed === key) return
  autoKm.pending = key
  setKmNote('暫定距離を取得中…')
  try {
    const r = await getRouteKm(ep.origin, ep.dest)
    // 取得中に手入力・ペア変更・モーダル閉鎖があれば書かない（上書き禁止を厳守）
    const g = D.form
    const ep2 = g === f ? shouldAutoKm({ kind: g.kind, online: D.online, quoteKm: g.quoteKm, stops: stopsOf(g) }) : null
    if (!ep2 || pairKey(ep2.origin, ep2.dest) !== key) return
    g.quoteKm = String(r.km)
    g.quoteDurMin = r.min > 0 ? r.min : '' // 所要時間（分）も一緒に保持（隠しデータ。人件費の時間ベース試算に使う）
    const inp = D.el && D.el.querySelector('#dp-modal [data-f="quoteKm"]')
    if (inp) inp.value = g.quoteKm
    setKmNote(`暫定・Google経路（手入力で上書き可）${r.min > 0 ? `・暫定 ${Math.round(r.min / 60 * 10) / 10}時間` : ''}`)
    updateCheck()
  } catch (e) {
    autoKm.failed = key // 同じペアで失敗を繰り返さない（距離欄を空にし直せば再試行）
    setKmNote('暫定距離を取得できませんでした（手入力してください）')
  } finally {
    if (autoKm.pending === key) autoKm.pending = ''
    if (D.form && D.form.kmNote === '暫定距離を取得中…') setKmNote('')
  }
}
function renderForm() {
  const f = D.form, own = f.kind === 'own'
  // 状態（仮受付/正式/配車済）。選択肢は canTransition で絞る＝既存 trip（savedStatus='sent'）は
  // 配車済のみ→固定表示。pend 中は傭車の支払系を出さない（requests に書かない項目。設計 方針3）
  const stNow = normalizeStatus(f.status)
  const pend = stNow !== 'sent'
  const stChoices = ['sashi', 'formal', 'sent'].filter((s) => !f.savedStatus || canTransition(f.savedStatus, s))
  const title = f.mode === 'add' ? '案件登録（受注・配車）' : '案件を編集'
  const staffOpts = [...new Set([...STAFF_BASE, ...D.tripsRaw.map((t) => t.staff).filter(Boolean), f.staff].filter(Boolean))]
  const prefSel = (i, sel) => `<select data-stop="${i}" data-sf="pref"><option value="">選択</option>${PREFS.map((x) => `<option value="${x}"${sel === x ? ' selected' : ''}>${x}</option>`).join('')}</select>`
  const specSel = (i, sel) => `<select data-stop="${i}" data-sf="spec"><option value=""${!sel ? ' selected' : ''}>時間指定なし</option>${['まで', '指定', '以降'].map((x) => `<option value="${x}"${sel === x ? ' selected' : ''}>${x}</option>`).join('')}</select>`
  const packOpts = ['パレット', 'バラ', 'カゴ', 'フレコン', 'コンテナ', '指定しない'] // 2026-08-25 オーナー決
  const stops = stopsOf(f)
  const runIdx = stops.indexOf(firstPick(f))
  // 地点カード（積地/着地）: data-stop=index／data-sf=フィールドでバインド。並び替え↑↓・積着切替・削除×付き。運行日＝最初の積地の日付。
  const stopCard = (s, i) => {
    const drop = s.role === 'drop'
    const canDel = (drop ? dropsOf(f).length : picksOf(f).length) > 1
    return `
    <div class="dp-pt" data-stopcard="${i}">
      <div class="dp-pt-h${drop ? ' b' : ''}">
        <span>地点${i + 1}　${drop ? '卸し（着）' : '積み（発）'}</span>
        <span class="dp-pt-tools">
          <button type="button" class="dp-ptbtn" data-move="up" data-i="${i}"${i === 0 ? ' disabled' : ''} title="上へ">↑</button>
          <button type="button" class="dp-ptbtn" data-move="down" data-i="${i}"${i === stops.length - 1 ? ' disabled' : ''} title="下へ">↓</button>
          <button type="button" class="dp-ptbtn" data-role="${i}" title="積地／着地を切替">${drop ? '積地に' : '着地に'}</button>
          <button type="button" class="dp-ptbtn del" data-del="${i}"${canDel ? '' : ' disabled'} title="この地点を削除">×</button>
        </span>
      </div>
      <div class="dp-pt-b">
        <div class="dp-grid3">
          ${field('日付' + (i === runIdx ? '（運行日）*' : ''), `<input type="date" data-stop="${i}" data-sf="date" value="${esc(s.date || '')}">`)}
          ${field('時間', `<input type="time" data-stop="${i}" data-sf="time" value="${esc(s.time || '')}">`)}
          ${field('時間指定', specSel(i, s.spec))}
        </div>
        <div style="margin-top:8px">${field('所名（拠点名）', `<input data-stop="${i}" data-sf="name" value="${esc(s.name || '')}" placeholder="拠点名・会社名（任意）">`)}</div>
        <div class="dp-grid3" style="margin-top:8px">
          ${field('都道府県', prefSel(i, s.pref))}
          ${field('市区町村', `<input data-stop="${i}" data-sf="city" value="${esc(s.city || '')}" placeholder="例）品川区8丁目">`)}
          ${field('番地', `<input data-stop="${i}" data-sf="addr" value="${esc(s.addr || '')}" placeholder="例）8-5">`)}
        </div>
        <div style="margin-top:8px">${field('建物名・部屋番号', `<input data-stop="${i}" data-sf="bldg" value="${esc(s.bldg || '')}" placeholder="例）ハコベルビル-805">`)}</div>
        <details class="dp-more"${(s.contact || s.tel || s.note) ? ' open' : ''}>
          <summary>連絡先・軒先条件</summary>
          <div class="dp-grid" style="margin-top:6px">
            ${field('連絡先（名前）', `<input data-stop="${i}" data-sf="contact" value="${esc(s.contact || '')}" placeholder="例）箱田太郎">`)}
            ${field('連絡先（電話）', `<input data-stop="${i}" data-sf="tel" value="${esc(s.tel || '')}" placeholder="例）08-8888-5555">`)}
          </div>
          <div style="margin-top:6px">${field('軒先条件', `<input data-stop="${i}" data-sf="note" value="${esc(s.note || '')}" placeholder="※軒先の注意事項があれば">`)}</div>
        </details>
      </div>
    </div>`
  }
  return `
  <div class="dp-modal" id="dp-modal">
    <div class="dp-card wide">
      <div class="dp-card-h">${title}<span class="dp-note">${f.mode === 'edit' ? '管理ID: ' + esc(f.id) : 'VEIL採番・手入力'}</span></div>
      <div class="dp-card-scroll">
        <div class="dp-seg" style="margin:2px 2px 12px">
          <button data-kind="own" class="${own ? 'on' : ''}">自社便</button>
          <button data-kind="charter" class="${!own ? 'on' : ''}">傭車</button>
        </div>
        <div class="dp-fld" style="margin:0 2px 12px"><span class="dp-lab">状態</span>
          ${stChoices.length > 1 ? `<div class="dp-radio" data-radio>
            ${stChoices.map((s) => `<label class="${stNow === s ? 'on' : ''}"><input type="radio" name="dp-status" data-st="${s}" value="${s}"${stNow === s ? ' checked' : ''}>${STATUS_LABEL[s]}</label>`).join('')}
          </div>${pend ? '<span class="dp-hint">仮受付・正式は件数・金額（KPI）や収支に入りません。配車済にすると受注・配車として確定します</span>' : ''}` : '<span class="dp-hint">配車済（配車済からは戻せません。取り下げは下の「この案件を取消」）</span>'}
        </div>
        <div class="dp-form2">
          <div class="dp-main">
            <div class="dp-sec" id="sec-nushi"><div class="dp-sec-h">荷主情報</div><div class="dp-grid">
              ${field('得意先', `<div class="dp-combo" data-combo="cust"><input data-party="cust" value="${esc(f.customerName || '')}" placeholder="コード または 会社名で検索" autocomplete="off"><div class="dp-combo-list" hidden></div></div>`, f.customerId ? 'マスタ確定（コード-枝番→正式名称）' : 'コード/会社名で検索して選択。無ければそのまま入力可')}
              ${field('担当', `<select data-f="staff"><option value=""></option>${staffOpts.map((s) => `<option value="${esc(s)}"${s === f.staff ? ' selected' : ''}>${esc(s)}</option>`).join('')}</select>`)}
            </div></div>
            <div class="dp-sec"><div class="dp-sec-h">実運送体制管理簿</div>
              <div class="dp-hint" style="margin:0 2px 7px">2025年〜 起票が必要。この運送業務で、自社より上に元請業者は存在しますか？</div>
              <div class="dp-radio" data-radio>
                ${['存在しない', '存在する', 'わからない'].map((o) => `<label class="${f.prime === o ? 'on' : ''}"><input type="radio" name="dp-prime" data-f="prime" value="${o}"${f.prime === o ? ' checked' : ''}>${o}</label>`).join('')}
              </div>
            </div>
            <div class="dp-sec" id="sec-route"><div class="dp-sec-h">ルートの設定</div>
              ${stops.map((s, i) => stopCard(s, i)).join('')}
              <div class="dp-addrow">
                <button type="button" class="dp-addbtn pick" data-add="pick">＋ 積地を追加</button>
                <button type="button" class="dp-addbtn drop" data-add="drop">＋ 着地を追加</button>
              </div>
              <div class="dp-hint" style="margin:7px 2px 0">地点は訪問順に並びます（↑↓で入替・「積地に/着地に」で種別変更）。運行日＝最初の積地の日付。住所は都道府県＋市区町村だけでもOK（旧データは所名に表示）。</div>
            </div>
            <div class="dp-sec" id="sec-sha"><div class="dp-sec-h">希望車種</div><div class="dp-grid">
              ${field('車種 *', vehicleTypeInput('data-f="vehicleClass"', f.vehicleClass, D.vtypes, 'dp-vtypes'))}
            </div></div>
            <div class="dp-sec" id="sec-nimotsu"><div class="dp-sec-h">荷物情報</div>
              <div>${field('品名 *', `<input data-f="item" value="${esc(f.item)}" placeholder="例）印刷物">`)}</div>
              <div class="dp-fld" style="margin-top:9px"><span class="dp-lab">荷姿</span>
                <div class="dp-radio" data-radio>
                  ${packOpts.map((o) => { const v = o === '指定しない' ? '' : o; return `<label class="${(f.packing || '') === v ? 'on' : ''}"><input type="radio" name="dp-pack" data-f="packing" value="${v}"${(f.packing || '') === v ? ' checked' : ''}>${o}</label>` }).join('')}
                </div>
              </div>
              <div class="dp-grid3" style="margin-top:9px">
                ${field('個数', `<input type="number" data-f="qty" class="num" value="${esc(f.qty)}">`)}
                ${field('重量(kg)', `<input type="number" data-f="weightKg" class="num" value="${esc(f.weightKg)}">`)}
                ${field('容積(m³)', `<input type="number" data-f="volumeM3" class="num" value="${esc(f.volumeM3)}">`)}
              </div>
              <div class="dp-grid" style="margin-top:9px">
                ${field('伝票番号', `<input data-f="slipNo" value="${esc(f.slipNo)}" placeholder="例）000000000">`)}
                ${field('荷物メモ', `<input data-f="cargoNote" value="${esc(f.cargoNote || '')}" placeholder="※荷物の注意事項があれば">`)}
              </div>
            </div>
            <div class="dp-sec" id="sec-tanto"><div class="dp-sec-h">${own ? '乗務員（自社便）' : '協力会社への配車依頼'}</div><div class="dp-grid">
              ${own
                ? field('自社乗務員 *', `<div class="dp-combo" data-combo="drv"><input data-party="drv" value="${esc(driverInit(f))}" placeholder="コード または 氏名で検索・直接入力" autocomplete="off"><div class="dp-combo-list" hidden></div></div>`, f.driverId ? 'マスタ確定' : (f.driverName ? '現在: ' + esc(f.driverName) + '（自由入力）' : 'コード/氏名で検索して選択・無ければ直接入力'))
                : field('協力会社 *', `<div class="dp-combo" data-combo="part"><input data-party="part" value="${esc(f.partnerName || '')}" placeholder="コード または 会社名で検索" autocomplete="off"><div class="dp-combo-list" hidden></div></div>`, f.partnerId ? 'マスタ確定' : 'コード/会社名で検索して選択。無ければ入力可')}
              ${own
                ? field('車番（任意）', `<input data-f="carNo" value="${esc(f.carNo)}" placeholder="例: 55（車両経費の精度UP）">`)
                : field('実運送会社', `<input data-f="carrier" value="${esc(f.carrier)}">`)}
              ${own ? '' : field('傭車乗務員', `<input data-f="youshaDriver" value="${esc(f.youshaDriver)}">`)}
              ${own ? '' : field('携帯番号', `<input data-f="tel" value="${esc(f.tel)}">`)}
            </div></div>
            <div class="dp-sec" id="sec-kingaku"><div class="dp-sec-h">金額・締め</div><div class="dp-grid">
              ${field('運賃（受注）*', `<input type="number" data-f="fare" class="num" value="${esc(f.fare)}">`)}
              ${own ? field('距離(km)（試算）', `<input type="number" data-f="quoteKm" class="num" value="${esc(f.quoteKm || '')}" placeholder="例）120">`, `${quoteFuelRate(f.vehicleClass).kmPerL}km/L × ${quoteFuelRate(f.vehicleClass).yenPerL}円/L<span id="dp-kmauto">${f.kmNote ? '　' + esc(f.kmNote) : ''}</span>`) : ''}
              ${own || pend ? '' : field('傭車金額（支払）', `<input type="number" data-f="charterCost" class="num" value="${esc(f.charterCost)}">`)}
              ${field('請通行料（任意）', `<input type="number" data-f="tollBill" class="num" value="${esc(f.tollBill)}">`)}
              ${own || pend ? '' : field('支通行料（任意）', `<input type="number" data-f="tollPay" class="num" value="${esc(f.tollPay)}">`)}
              ${field('請求確定区分', `<select data-f="billFixed"><option value=""${!f.billFixed ? ' selected' : ''}></option><option value="速報"${f.billFixed === '速報' ? ' selected' : ''}>速報</option><option value="確定"${f.billFixed === '確定' ? ' selected' : ''}>確定</option></select>`)}
              ${own || pend ? '' : field('支払確定区分', `<select data-f="payFixed"><option value=""${!f.payFixed ? ' selected' : ''}></option><option value="速報"${f.payFixed === '速報' ? ' selected' : ''}>速報</option><option value="確定"${f.payFixed === '確定' ? ' selected' : ''}>確定</option></select>`)}
              ${pend && !own ? '<span class="dp-hint" style="grid-column:1/-1">傭車の支払系（傭車金額・支通行料・支払確定区分）は配車済にしてから入力します（仮受付・正式では保存されません）</span>' : ''}
              ${field('売上日', `<input type="date" data-f="salesDate" value="${esc(f.salesDate)}">`)}
              ${field('受領書回収日', `<input type="date" data-f="receiptDate" value="${esc(f.receiptDate)}">`)}
              ${field('備考①（請求／ハコベル管理NO）', `<input data-f="memo1" value="${esc(f.memo1)}" placeholder="例 260731-02K-RXN">`)}
              ${field('備考②（運行）', `<input data-f="memo2" value="${esc(f.memo2)}">`)}
            </div></div>
            ${f.err ? `<div class="dp-err">${esc(f.err)}</div>` : ''}
          </div>
          <aside class="dp-side" id="dp-check">${sidePanel(f)}</aside>
        </div>
      </div>
      <div class="dp-card-f">
        ${f.mode === 'edit'
          ? (f.isRequest
            ? `<button class="dp-btn ghost" id="dp-delreq">この受付を削除</button>`
            : (f.confirmVoid ? `<button class="dp-btn danger" id="dp-void2">本当に取消？（収支から除外）</button>` : `<button class="dp-btn ghost" id="dp-void">この案件を取消</button>`))
          : `<label class="dp-hint" style="display:flex;align-items:center;gap:5px;cursor:pointer"><input type="checkbox" id="dp-cont" ${f.cont ? 'checked' : ''}>保存して連続入力</label>`}
        <span style="flex:1"></span>
        <button class="dp-btn" id="dp-cancel">閉じる</button>
        <button class="dp-btn primary" id="dp-save">${esc(D.saving || (f.mode === 'add' && f.cont ? '保存して次へ' : '保存'))}</button>
      </div>
    </div>
  </div>`
}
function wireForm() {
  const el = D.el
  el.querySelectorAll('#dp-modal [data-kind]').forEach((b) => b.addEventListener('click', () => { D.form.kind = b.dataset.kind; D.form.err = ''; paint() }))
  // 状態（仮受付/正式/配車済）の切替。支払系フィールドの表示が変わるので再描画
  el.querySelectorAll('#dp-modal [data-st]').forEach((r) => r.addEventListener('change', () => { D.form.status = r.value; D.form.err = ''; paint() }))
  el.querySelectorAll('#dp-modal [data-f]').forEach((inp) => {
    const ev = (inp.tagName === 'SELECT' || inp.type === 'radio' || inp.type === 'checkbox') ? 'change' : 'input'
    inp.addEventListener(ev, () => {
      D.form[inp.dataset.f] = inp.value; updateCheck()
      // 距離欄: 手入力したら「暫定」注記を消す。空に戻したら所要時間も捨てて再取得（失敗の抑制も解除）
      if (inp.dataset.f === 'quoteKm') { setKmNote(''); if (!String(inp.value).trim()) { D.form.quoteDurMin = ''; autoKm.failed = ''; scheduleAutoKm() } }
    })
  })
  // 地点（stops）の各フィールド: data-stop=index / data-sf=field でネスト更新（再描画不要）
  el.querySelectorAll('#dp-modal [data-stop]').forEach((inp) => {
    const ev = inp.tagName === 'SELECT' ? 'change' : 'input'
    inp.addEventListener(ev, () => {
      const s = stopsOf(D.form)[Number(inp.dataset.stop)]
      if (s) {
        s[inp.dataset.sf] = inp.value; updateCheck()
        // 積卸地の住所が変わったら暫定距離を取り直す（デバウンス。距離欄が空のときだけ動く）
        if (inp.dataset.sf === 'pref' || inp.dataset.sf === 'city') scheduleAutoKm()
      }
    })
  })
  // 地点の追加／削除／並び替え／積着切替（構造が変わるので再描画）
  el.querySelectorAll('#dp-modal [data-add]').forEach((b) => b.addEventListener('click', () => { D.form.stops = stopsOf(D.form).concat([blankStop(b.dataset.add)]); paint() }))
  el.querySelectorAll('#dp-modal [data-del]').forEach((b) => b.addEventListener('click', () => {
    const i = Number(b.dataset.del), st = stopsOf(D.form), s = st[i]; if (!s) return
    if ((s.role === 'drop' ? dropsOf(D.form) : picksOf(D.form)).length <= 1) return
    st.splice(i, 1); paint()
  }))
  el.querySelectorAll('#dp-modal [data-move]').forEach((b) => b.addEventListener('click', () => {
    const i = Number(b.dataset.i), st = stopsOf(D.form), j = i + (b.dataset.move === 'up' ? -1 : 1)
    if (j < 0 || j >= st.length) return
    const t = st[i]; st[i] = st[j]; st[j] = t; paint()
  }))
  el.querySelectorAll('#dp-modal [data-role]').forEach((b) => b.addEventListener('click', () => {
    const s = stopsOf(D.form)[Number(b.dataset.role)]; if (!s) return
    s.role = s.role === 'drop' ? 'pick' : 'drop'; paint()
  }))
  // ラジオ（実運送体制・荷姿）のピル選択ハイライトを即時反映
  el.querySelectorAll('#dp-modal .dp-radio').forEach((grp) => grp.addEventListener('change', () => {
    grp.querySelectorAll('label').forEach((lab) => { const r = lab.querySelector('input'); lab.classList.toggle('on', !!(r && r.checked)) })
  }))
  // 右パネルのチェック項目クリックで該当セクションへスクロール
  el.querySelectorAll('#dp-modal [data-goto]').forEach((g) => g.addEventListener('click', () => { const t = el.querySelector('#' + g.dataset.goto); if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' }) }))
  // 得意先/協力会社: コード先頭・名前揃えの検索ドロップダウン（コードでも会社名でも絞り込み）。
  el.querySelectorAll('#dp-modal .dp-combo').forEach(setupCombo)
  // モーダルを開いた/再描画した時点でも暫定距離を判定（編集で開いた既存案件・地点の追加/入替後など）
  scheduleAutoKm()
  const cont = el.querySelector('#dp-cont'); if (cont) cont.addEventListener('change', () => { D.form.cont = cont.checked })
  const save = el.querySelector('#dp-save'); if (save) save.addEventListener('click', saveForm)
  const cancel = el.querySelector('#dp-cancel'); if (cancel) cancel.addEventListener('click', () => { D.form = null; paint() })
  const modal = el.querySelector('#dp-modal'); if (modal) modal.addEventListener('mousedown', (e) => { if (e.target === modal) { D.form = null; paint() } })
  const v1 = el.querySelector('#dp-void'); if (v1) v1.addEventListener('click', () => { D.form.confirmVoid = true; paint() })
  const v2 = el.querySelector('#dp-void2'); if (v2) v2.addEventListener('click', voidForm)
  // 仮受付/正式（requests）の誤登録は物理削除（intake の「この受付を削除」と同じ挙動。trips の「取消」とは別物）
  const dr = el.querySelector('#dp-delreq')
  if (dr) dr.addEventListener('click', async () => {
    const f = D.form
    if (!f.id || !confirm('この受付（仮受付/正式）を削除しますか？')) return
    try {
      D.saving = '削除中…'; paint()
      await remove('requests', f.id)
      D.reqsRaw = (D.reqsRaw || []).filter((r) => r.id !== f.id)
      rebuildRows(); D.form = null; D.saving = '削除しました ✓'; paint()
      setTimeout(() => { D.saving = ''; paint() }, 2200)
    } catch (e) { D.saving = ''; f.err = '削除に失敗しました'; paint() }
  })
}

// 得意先/協力会社の検索ドロップダウン。コード先頭＋会社名を列揃え。コードでも会社名でも部分一致で絞り込み。
function setupCombo(box) {
  const which = box.dataset.combo // 'cust' | 'part' | 'drv'
  const isDrv = which === 'drv'
  const src = which === 'cust' ? D.cust : which === 'part' ? D.part : D.drv
  const input = box.querySelector('input')
  const list = box.querySelector('.dp-combo-list')
  const nmeOf = (c) => isDrv ? (c.name || '') : (c.formal || c.name || '')
  const disp = (c) => isDrv
    ? String(c.code != null ? c.code : (c.id || ''))
    : `${c.code != null ? c.code : ''}${c.branchNo != null && c.branchNo !== '' ? '-' + c.branchNo : ''}`
  // コード昇順（数値優先）→枝番→名称順に並べて一覧を見やすく
  const arr = (src || []).slice().sort((a, b) => {
    const ca = Number(a.code), cb = Number(b.code)
    if (Number.isFinite(ca) && Number.isFinite(cb) && ca !== cb) return ca - cb
    const ea = Number(a.branchNo), eb = Number(b.branchNo)
    if (Number.isFinite(ea) && Number.isFinite(eb) && ea !== eb) return ea - eb
    return String(nmeOf(a)).localeCompare(String(nmeOf(b)), 'ja')
  })
  const setId = (id, nm) => {
    if (which === 'cust') { D.form.customerId = id; D.form.customerName = nm }
    else if (which === 'part') { D.form.partnerId = id; D.form.partnerName = nm }
    else { D.form.driverId = id; D.form.driverName = nm }
    updateCheck()
  }
  const curId = () => which === 'cust' ? D.form.customerId : which === 'part' ? D.form.partnerId : D.form.driverId
  let cur = [], hi = -1
  const place = () => { const r = input.getBoundingClientRect(); list.style.left = r.left + 'px'; list.style.top = (r.bottom + 3) + 'px'; list.style.width = r.width + 'px' }
  const hide = () => { list.hidden = true; hi = -1 }
  const pick = (c) => { setId(c.id, nmeOf(c)); input.value = `${disp(c)}　${nmeOf(c)}`; hide() }
  const paintRows = () => {
    list.innerHTML = cur.length
      ? cur.map((c, i) => `<div class="dp-combo-row${i === hi ? ' active' : ''}" data-i="${i}"><span class="cc">${esc(disp(c))}</span><span class="cn">${esc(nmeOf(c) || c.id)}</span></div>`).join('')
      : '<div class="dp-combo-empty">一致なし（このまま入力すると自由記入で保存）</div>'
  }
  const render = (filter) => {
    const s = String(filter == null ? '' : filter).trim().toLowerCase()
    cur = arr.filter((c) => { const nm = String(nmeOf(c)).toLowerCase(); return !s || nm.includes(s) || disp(c).toLowerCase().includes(s) }).slice(0, 200)
    hi = cur.length ? 0 : -1
    place(); list.hidden = false; paintRows()
  }
  const toHi = () => { const row = list.querySelector('.dp-combo-row.active'); if (row) row.scrollIntoView({ block: 'nearest' }) }
  // クリック（フォーカス）で全件一覧をせり出す＋テキスト選択（そのまま打てば絞り込み）
  input.addEventListener('focus', () => { render(''); try { input.select() } catch (e) {} })
  input.addEventListener('input', () => { setId('', String(input.value || '').trim()); render(input.value) })
  input.addEventListener('keydown', (e) => {
    if (list.hidden && e.key === 'ArrowDown') { render(input.value); e.preventDefault(); return }
    if (list.hidden) return
    if (e.key === 'ArrowDown') { hi = Math.min(hi + 1, cur.length - 1); paintRows(); toHi(); e.preventDefault() }
    else if (e.key === 'ArrowUp') { hi = Math.max(hi - 1, 0); paintRows(); toHi(); e.preventDefault() }
    else if (e.key === 'Enter') { if (cur[hi]) { pick(cur[hi]); e.preventDefault() } }
    else if (e.key === 'Escape') { hide() }
  })
  list.addEventListener('mousedown', (e) => { const row = e.target.closest('.dp-combo-row'); if (!row) return; e.preventDefault(); const c = cur[Number(row.dataset.i)]; if (c) pick(c) })
  input.addEventListener('blur', () => { setTimeout(hide, 160); if (!curId()) { const hit = resolveCombo(which, arr, input.value); if (hit) pick(hit) } })
  const scroller = box.closest('.dp-card-scroll')
  if (scroller) scroller.addEventListener('scroll', () => { if (!list.hidden) place() }, { passive: true })
}

async function saveForm() {
  const f = D.form
  const st = normalizeStatus(f.status)
  const pend = saveTarget(st) === 'requests' // 仮受付/正式 → requests（KPI・収支の母数 trips に入れない）
  const p0 = firstPick(f), dN = lastDrop(f)
  const runDate = (p0 && p0.date) || f.date || ''
  if (!runDate) { f.err = '運行日（最初の積地の日付）を入力してください'; paint(); return }
  // 仮受付/正式は運行日のみ必須（電話段階の仮の話を書き留める用途。intake と同じ緩さ）。
  // 配車済は現行の必須のまま＝既定（配車済）の挙動は不変
  if (!pend) {
    if (!dN) { f.err = '着地（卸し地点）を1つ以上入れてください'; paint(); return }
    if (f.fare === '' || f.fare == null) { f.err = '運賃を入力してください'; paint(); return }
    if (f.kind === 'own' && !f.driverId && !f.driverName) { f.err = '乗務員を選択してください（自社便）'; paint(); return }
    if (f.kind === 'charter' && !f.partnerId && !f.partnerName) { f.err = '協力会社を選択してください（傭車）'; paint(); return }
  }
  const id = f.id || newManageId(runDate, D.user && D.user.email)
  const cust = f.customerId ? D.custById[f.customerId] : null
  const cleanStops = stopsOf(f).map((s) => ({ role: s.role === 'drop' ? 'drop' : 'pick', name: s.name || '', pref: s.pref || '', city: s.city || '', addr: s.addr || '', bldg: s.bldg || '', date: s.date || '', time: s.time || '', spec: s.spec || '', contact: s.contact || '', tel: s.tel || '', note: s.note || '' }))
  const doc = {
    source: f.source || 'manual', manageId: id, date: runDate, unloadDate: (dN && dN.date) || runDate, month: runDate.slice(0, 7),
    staff: f.staff || '', kind: f.kind, tripType: f.tripType || 'spot', voided: false,
    customerName: cust ? (cust.name || '') : (f.customerName || ''),
    customerFormal: cust ? (cust.formal || cust.name || '') : (f.customerName || ''),
    origin: originStr(f), dest: destStr(f), item: f.item || '', vehicleClass: f.vehicleClass || '',
    stops: cleanStops, cargoNote: f.cargoNote || '',
    loadTime: (p0 && p0.time) || '', unloadTime: (dN && dN.time) || '', loadSpec: (p0 && p0.spec) || '', unloadSpec: (dN && dN.spec) || '',
    salesDate: f.salesDate || '', receiptDate: f.receiptDate || '', memo1: f.memo1 || '', memo2: f.memo2 || '',
    packing: f.packing || '', qty: Number(f.qty) || 0, weightKg: Number(f.weightKg) || 0, volumeM3: Number(f.volumeM3) || 0, prime: f.prime || '', slipNo: f.slipNo || '',
    ankenNo: (f.memo1 && HAKO_NO.test(String(f.memo1).trim())) ? String(f.memo1).trim() : '', billFixed: f.billFixed || '',
    fare: Number(f.fare) || 0, tollBill: Number(f.tollBill) || 0, updatedAt: new Date().toISOString(),
  }
  if (cust) { doc.customerCode = cust.code; if (cust.branchNo != null) doc.customerBranchNo = cust.branchNo }
  if (f.kind === 'own') {
    const drv = f.driverId ? D.drvById[f.driverId] : null
    doc.driverName = drv ? (drv.name || '') : (f.driverName || '')
    doc.driverCode = drv ? String(drv.code || drv.id) : ''
    doc.carNo = f.carNo || ''; doc.vehicleId = f.carNo || ''
    doc.partnerName = ''; doc.partnerFormal = ''; doc.charterCost = 0; doc.tollPay = 0; doc.tollCost = doc.tollBill
    doc.carrier = ''; doc.youshaDriver = ''; doc.tel = ''; doc.payFixed = ''
  } else {
    const pt = f.partnerId ? D.partById[f.partnerId] : null
    doc.partnerName = pt ? (pt.name || '') : (f.partnerName || '')
    doc.partnerFormal = pt ? (pt.formal || pt.name || '') : (f.partnerName || '')
    if (pt) { doc.partnerCode = pt.code; if (pt.branchNo != null) doc.partnerBranchNo = pt.branchNo }
    doc.charterCost = Number(f.charterCost) || 0; doc.tollPay = Number(f.tollPay) || 0
    doc.driverName = ''; doc.driverCode = ''; doc.carNo = ''; doc.vehicleId = ''; doc.tollCost = doc.tollPay
    doc.carrier = f.carrier || ''; doc.youshaDriver = f.youshaDriver || ''; doc.tel = f.tel || ''; doc.payFixed = f.payFixed || ''
  }
  try {
    D.saving = '保存中…'; paint()
    if (pend) {
      // 仮受付/正式 → requests（設計 方針1・3）。下払い系（charterCost/tollPay/payFixed）は
      // sanitizeForCustomer で落とす（intake と同じ不変条件。requests は公開リンク経由で得意先に見え得る）。
      // tollCost（支払通行料由来）・voided（trips 専用）も requests には書かない。公開リンクは発行しない
      const rdoc = sanitizeForCustomer({ ...doc, entry: 'dispatch', status: st })
      delete rdoc.tollCost; delete rdoc.voided
      await upsert('requests', id, rdoc)
      const i = (D.reqsRaw || []).findIndex((x) => x.id === id)
      const merged = { id, ...(i >= 0 ? D.reqsRaw[i] : {}), ...rdoc }
      if (i >= 0) D.reqsRaw[i] = merged; else D.reqsRaw.push(merged)
    } else {
      await upsert('trips', id, doc)
      // 自分の操作は即時差分反映（全件listはコスト高。realtimeでも届く）
      const i = D.tripsRaw.findIndex((x) => x.id === id)
      const merged = { id, ...(i >= 0 ? D.tripsRaw[i] : {}), ...doc }
      if (i >= 0) D.tripsRaw[i] = merged; else D.tripsRaw.push(merged)
      if (f.isRequest && f.id) {
        // 昇格（仮受付/正式 → 配車済）。intake の「取込する」と同じ形で requests に取込印を打つ。
        // IDは request の管理IDを trip がそのまま引き継ぐ（二重採番しない）。
        // パッチが失敗しても trip は保存済みなので止めない（次回読込で未取込に見えたら削除で直せる）
        try { await upsert('requests', f.id, { tripId: id, status: 'formal', dispatchedAt: new Date().toISOString() }) } catch (e) {}
        D.reqsRaw = (D.reqsRaw || []).filter((r) => r.id !== f.id)
      }
    }
    rebuildRows(); D.month = doc.month || D.month
    if (f.mode === 'add' && f.cont) {
      const carry = { date: runDate, vehicleClass: f.vehicleClass, staff: f.staff, customerId: f.customerId, driverId: f.driverId, partnerId: f.partnerId, carNo: f.carNo, status: st }
      D.form = blankForm(f.kind, carry); D.saving = '保存 ✓ 続けて入力できます'
    } else { D.form = null; D.saving = '保存しました ✓' }
    paint()
    setTimeout(() => { if (D.saving.indexOf('保存') === 0) { D.saving = ''; paint() } }, 1800)
  } catch (e) { D.saving = ''; f.err = '保存に失敗しました（サインイン状態と許可アカウントを確認）'; paint() }
}
async function voidForm() {
  const f = D.form; if (!f.id) return
  try {
    D.saving = '取消中…'; paint()
    await upsert('trips', f.id, { kind: 'other', voided: true, updatedAt: new Date().toISOString() })
    const trips = await listDocs('trips'); D.tripsRaw = trips || []
    rebuildRows(); D.form = null; D.saving = '取消しました（収支から除外） ✓'; paint()
    setTimeout(() => { D.saving = ''; paint() }, 2200)
  } catch (e) { D.saving = ''; f.err = '取消に失敗しました'; paint() }
}
function openEdit(id) { const t = D.tripsRaw.find((x) => x.id === id); if (!t) return; D.form = tripToForm(t); paint() }
function openEditRequest(id) { const r = (D.reqsRaw || []).find((x) => x.id === id); if (!r) return; D.form = requestToDispForm(r); paint() }

const SRC = [['all', '全部'], ['自社便', '自社便'], ['傭車', '傭車']]
const badgeColor = (s) => s === '自社便' ? 'var(--text)' : (s === '傭車' ? 'var(--signal)' : 'var(--text)')

// 状態セグメント（仮受付/正式/配車済=運行実績）。_request（requests由来・未取込）だけが
// 仮受付/正式を持ち、trip/order（配車済）はまとめて「運行実績」とする（設計 方針2）。
const SEG = [['all', '全部'], ['sashi', '仮受付'], ['formal', '正式'], ['sent', '配車済']]
const rowState = (o) => o._request ? (o.status === '正式' ? 'formal' : 'sashi') : 'sent'
// 状態バッジの色分け（スミ決 2026-08-26・design-スミ/guidelines/2026-08-24-管制室UI-UIUX仕様書_v1.md §7）。
// 警告色相当の新トークンは導入しない。既存4色（accent/pos/neg/signal）の使い分けで表現する。
const STATE_LABEL = { sashi: '仮受付', formal: '正式', sent: '運行実績' }
const STATE_COLOR = { sashi: ['var(--signal)', 'transparent'], formal: ['var(--pos)', 'var(--head)'], sent: ['var(--text)', 'var(--head)'] }

const COLS = [
  { key: 'source', label: '区分', cell: (o) => `<td><span class="dp-badge" style="color:${badgeColor(o.source)};background:color-mix(in srgb, ${badgeColor(o.source)} 12%, transparent)">${esc(o.source)}</span>${o.via && o.via !== '日計表' ? `<span class="dp-via">${esc(o.via)}</span>` : ''}</td>` },
  { key: 'id', label: '案件/管理番号', cell: (o) => `<td>${esc(o.id)}</td>` },
  { key: 'staff', label: '担当', cell: (o) => `<td>${esc(o.staff)}</td>` },
  { key: 'shipper', label: '荷主/得意先', cell: (o) => `<td>${esc(o.shipper)}</td>` },
  { key: 'startDate', label: '日付', cell: (o) => `<td>${esc(o.startDate)}${o.startTime ? ' ' + esc(o.startTime) : ''}</td>` },
  { key: 'from', label: '発', cell: (o) => `<td>${esc((o.startPref || '') + (o.startCity || ''))}</td>` },
  { key: 'to', label: '着', cell: (o) => `<td>${esc((o.endPref || '') + (o.endCity || ''))}</td>` },
  { key: 'item', label: '品名', cell: (o) => `<td>${esc(o.item)}</td>` },
  { key: 'vehReq', label: '車両', cell: (o) => `<td>${esc([o.vehReq, o.vehSize].filter(Boolean).join(' '))}</td>` },
  { key: 'orderAmount', label: '受注/運賃', num: 1, cell: (o) => `<td class="n">${YEN(o.orderAmount)}</td>` },
  { key: 'billToll', label: '高速料金', num: 1, cell: (o) => `<td class="n">${o.billToll ? YEN(o.billToll) : '—'}</td>` },
  { key: 'partner', label: '協力会社', cell: (o) => `<td>${esc(o.partner)}</td>` },
  { key: 'driver', label: 'ドライバー', cell: (o) => `<td>${esc(o.driver)}</td>` },
  { key: 'vehicle', label: '車番', cell: (o) => `<td>${esc(o.vehicle)}</td>` },
  { key: 'status', label: '状態', cell: (o) => { const st = rowState(o); const [c, bg] = STATE_COLOR[st]; return `<td><span class="dp-badge" style="color:${c};background:${bg}">${esc(STATE_LABEL[st])}</span></td>` } },
]

function sortRows(arr) {
  const { key, dir } = D.sort; const mul = dir === 'asc' ? 1 : -1
  const val = (o) => key === 'from' ? (o.startPref || '') + (o.startCity || '') : key === 'to' ? (o.endPref || '') + (o.endCity || '') : o[key]
  return [...arr].sort((a, b) => {
    const av = val(a), bv = val(b)
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mul
    return String(av == null ? '' : av).localeCompare(String(bv == null ? '' : bv), 'ja') * mul
  })
}

function paint(ctx) {
  const el = D.el
  const inScope = (o) => o.month === D.month && (D.src === 'all' || o.source === D.src) && (D.scope !== 'mine' || !D.myStaff || o.staff === D.myStaff)
  const rows = D.rows.filter(inScope)
  // KPIの母数は配車済（trips/orders）だけ。仮受付/正式（D.pendingRequests）は混ぜない
  // （pm-ミチ「仮受付は KPI に混ぜない」を正式にも拡張適用。設計 方針2・方針4）
  const n = rows.length
  const amt = rows.reduce((s, o) => s + (Number(o.orderAmount) || 0), 0)
  const toll = rows.reduce((s, o) => s + (Number(o.billToll) || 0), 0)
  const cO = rows.filter((o) => o.source === '自社便').length
  const cC = rows.filter((o) => o.source === '傭車').length
  const cManual = rows.filter((o) => o.via === '手入力').length
  const pending = (D.pendingRequests || []).filter(inScope)
  const opts = D.months.map((m) => `<option value="${m}" ${m === D.month ? 'selected' : ''}>${m.replace('-', '年')}月</option>`).join('')
  const srcbar = SRC.map(([id, lab]) => `<button data-src="${id}" class="${D.src === id ? 'on' : ''}">${lab}</button>`).join('')
  const segbar = SEG.map(([id, lab]) => `<button data-seg="${id}" class="${D.seg === id ? 'on' : ''}">${lab}</button>`).join('')
  // 一覧の表示だけは配車済＋仮受付/正式を合成する（統合の一覧側。データ・KPIは分けたまま＝設計 方針1(c)）
  const sorted = sortRows(rows.concat(pending))
  // セグメントタブ（仮受付/正式/配車済/全部）は表示の絞り込みのみ。KPI（n/amt/toll/cO/cC/cManual）は
  // rows（trips/orders）だけを母数にしたまま変えない（設計 方針2・PR④）
  const segFiltered = D.seg === 'all' ? sorted : sorted.filter((o) => rowState(o) === D.seg)
  const shown = segFiltered.slice(0, CAP)
  const totalListed = rows.length + pending.length
  // 件数の出し方は仕様書§7「絞り込み後は『851件中 62件』」に合わせる
  const capCount = D.seg === 'all'
    ? `${n}件${pending.length ? `・仮受付/正式${pending.length}件` : ''}`
    : `${totalListed}件中 ${segFiltered.length}件`
  const ths = COLS.map((c) => {
    const on = D.sort.key === c.key; const arw = on ? `<span class="arw">${D.sort.dir === 'asc' ? '▲' : '▼'}</span>` : ''
    return `<th class="${c.num ? 'n' : ''}" data-key="${c.key}" data-type="${c.num ? 'num' : 'text'}">${c.label}${arw}</th>`
  }).join('')
  const body = shown.map((o) => {
    if (o._trip) return `<tr class="clk" data-edit="${esc(o.id)}">${COLS.map((c) => c.cell(o)).join('')}</tr>`
    // dispatch 生まれ（手入力）の仮受付/正式は配車フォームで編集（状態の昇格もここ）。
    // 受付フォーム/メール取込生まれは従来どおり受付側で開く（行の由来でフォームを切替＝統合設計 方針2）
    if (o._request && o._entry === 'dispatch') return `<tr class="clk" data-edit-request="${esc(o.id)}" title="クリックで編集（状態の変更もここ）">${COLS.map((c) => c.cell(o)).join('')}</tr>`
    if (o._request) return `<tr class="clk" data-open-request="${esc(o.id)}" title="受付で開く">${COLS.map((c) => c.cell(o)).join('')}</tr>`
    return `<tr>${COLS.map((c) => c.cell(o)).join('')}</tr>`
  }).join('')
  // 一覧の状態表示（段3・5状態パターン）。表示部分のみ＝フォーム/モーダル/KPI/バッジは不変。
  // 実fetch失敗（デモ・閲覧のみの意図とは別）は帯で可視化。dispatchはサンプルへのフォールバックを
  // 持たないため、保存済みデータが表示できていないことを明記する
  const errBanner = D.fetchError
    ? '<div class="ct-state is-error"><b class="ct-state__t">最新データの取得に失敗しました</b><span>保存済みの案件を表示できていません。接続を確認して再試行してください</span><button type="button" class="btn" id="dp-retry">再試行</button></div>'
    : ''
  // 空（未登録: 案件データ自体が0件）と条件不一致（区分/状態/担当の絞り込みで0件）を区別する
  const anyData = D.rows.length + (D.pendingRequests || []).length > 0
  const listState = !shown.length
    ? (anyData
      ? '<div class="ct-state is-nomatch"><b class="ct-state__t">この条件に一致する案件はありません</b><span>区分・状態・担当の絞り込みで0件になっています</span><button type="button" class="btn" id="dp-clearfilter">絞り込みをクリア</button></div>'
      : (D.fetchError ? '' : '<div class="ct-state is-empty"><b class="ct-state__t">案件データがまだありません</b><span>サインイン後「＋スポット便」で登録するか、日計表の取込・受付経由で案件が入ると表示されます</span></div>'))
    : ''
  el.innerHTML = `
    <section class="dp">
      <div class="dp-bar">
        <button class="dp-btn primary" id="dp-add" ${D.online ? '' : 'disabled'}>＋スポット便</button>
        <button class="dp-btn" id="dp-teiki">定期便マスタ / 生成</button>
        <select id="dp-month">${opts}</select>
        <div class="dp-seg">${srcbar}</div>
        ${D.user ? `<span class="dp-note" style="display:inline-flex;align-items:center;gap:5px">自分の担当 <select id="dp-setmine" title="担当は後からいつでも変更できます"><option value=""${!D.myStaff ? ' selected' : ''}>${D.myStaff ? '未設定に戻す' : '担当を選択…'}</option>${[...new Set([...STAFF_BASE, D.myStaff].filter(Boolean))].map((s) => `<option value="${esc(s)}"${s === D.myStaff ? ' selected' : ''}>${esc(s)}</option>`).join('')}</select></span>` : ''}
        ${D.myStaff ? `<div class="dp-seg"><button data-scope="mine" class="${D.scope === 'mine' ? 'on' : ''}">自分(${esc(D.myStaff)})</button><button data-scope="all" class="${D.scope === 'all' ? 'on' : ''}">全社</button></div>` : ''}
        ${D.live ? '<span class="dp-save" style="background:var(--head);border-color:var(--e1)"><i style="display:inline-block;width:7px;height:7px;border-radius:50%;background:var(--pos);margin-right:5px;vertical-align:0"></i>リアルタイム</span>' : ''}
        ${D.saving ? `<span class="dp-save">${esc(D.saving)}</span>` : ''}
        ${D.online ? '' : '<span class="dp-note">閲覧のみ（サインインで入力・保存）。</span>'}
      </div>
      <div class="dp-bar">
        <div class="dp-seg">${segbar}</div>
      </div>
      ${errBanner}
      <div class="dp-kpis">
        <div class="dp-kpi acc"><div class="k">件数</div><div class="v">${n}<span style="font-size:12px"> 件</span></div></div>
        <div class="dp-kpi"><div class="k">受注・運賃 計</div><div class="v">${YEN(amt)}</div></div>
        <div class="dp-kpi"><div class="k">高速料金 計</div><div class="v">${YEN(toll)}</div></div>
        <div class="dp-kpi"><div class="k">区分内訳</div><div class="v" style="font-size:13px">自社${cO} / 傭車${cC}<span style="font-size:10px;color:var(--text)"> ・手入力${cManual}</span></div></div>
      </div>
      <div class="dp-wrap">
        <div class="cap">受注・配車一覧（${capCount}・${(D.month || '').replace('-', '年')}月／行クリックで編集・見出しで並び替え）${n > CAP ? `<span style="font-weight:400;color:var(--neg)"> ※先頭${CAP}件を表示</span>` : ''}</div>
        ${listState || `<div class="dp-scroll"><table class="dp-tbl">
          <thead><tr>${ths}</tr></thead><tbody>${body}</tbody></table></div>`}
      </div>
      <div class="dp-note">案件はVEILで直接入力（＋案件を追加／行クリックで編集・取消）。得意先・協力会社はCD＋枝番で選ぶと正式名称・コードが自動保存され、収支・帳票の名寄せに合流します。管理IDはVEIL採番（手入力＝手入力バッジ）。日計表の自社便・傭車＋ハコベル（担当＝近藤・傭車のみ）を統合表示。取消は収支・ボードから除外（物理削除はしません）。</div>
      ${D.form ? renderForm() : ''}
    </section>`
  el.querySelector('#dp-month').addEventListener('change', (e) => { D.month = e.target.value; paint(ctx) })
  const retry = el.querySelector('#dp-retry'); if (retry) retry.addEventListener('click', () => { renderDispatch(D.el, D.ctx || {}) })
  const clearF = el.querySelector('#dp-clearfilter'); if (clearF) clearF.addEventListener('click', () => { D.src = 'all'; D.seg = 'all'; D.scope = 'all'; D._scopeTouched = true; paint(ctx) })
  el.querySelectorAll('[data-src]').forEach((b) => b.addEventListener('click', () => { D.src = b.dataset.src; paint(ctx) }))
  el.querySelectorAll('[data-seg]').forEach((b) => b.addEventListener('click', () => { D.seg = b.dataset.seg; paint(ctx) }))
  const addBtn = el.querySelector('#dp-add'); if (addBtn) addBtn.addEventListener('click', () => { D.form = blankForm('own'); paint(ctx) })
  const teikiBtn = el.querySelector('#dp-teiki'); if (teikiBtn) teikiBtn.addEventListener('click', () => { location.hash = '#/teiki' })
  el.querySelectorAll('[data-scope]').forEach((b) => b.addEventListener('click', () => { D.scope = b.dataset.scope; D._scopeTouched = true; paint(ctx) }))
  const setmine = el.querySelector('#dp-setmine'); if (setmine) setmine.addEventListener('change', async () => { if (!D.user) return; const v = setmine.value; try { await upsert('users', D.user.email, { staff: v, email: D.user.email, updatedAt: new Date().toISOString() }); D.myStaff = v; D.scope = v ? 'mine' : 'all'; D._scopeTouched = true; paint(ctx) } catch (e) {} })
  el.querySelectorAll('th[data-key]').forEach((th) => th.addEventListener('click', () => {
    const key = th.dataset.key
    if (D.sort.key === key) D.sort.dir = D.sort.dir === 'asc' ? 'desc' : 'asc'
    else D.sort = { key, dir: th.dataset.type === 'text' ? 'asc' : 'desc' }
    paint(ctx)
  }))
  el.querySelectorAll('tr[data-edit]').forEach((tr) => tr.addEventListener('click', () => openEdit(tr.dataset.edit)))
  el.querySelectorAll('tr[data-edit-request]').forEach((tr) => tr.addEventListener('click', () => openEditRequest(tr.dataset.editRequest)))
  // 仮受付/正式の行（未取込）は配車フォームを持たない。受付側で開く（統合の導線。設計 方針5・PR②）
  el.querySelectorAll('tr[data-open-request]').forEach((tr) => tr.addEventListener('click', () => { location.hash = '#/intake?open=' + encodeURIComponent(tr.dataset.openRequest) }))
  if (D.form) wireForm()
}
